import { events } from '@/app/events/events';
import { RefreshType } from '@/app/shared/types/RefreshType';
import { VERSION } from '@/shared/constants/appConstants';

// Throttle /version.json checks so repeated failures (e.g. a loader firing
// several API calls at once) don't re-check more than once per interval.
const MIN_RECHECK_INTERVAL_MS = 60_000;

let lastCheckedAt = 0;
let refreshRequested = false;
let inFlight = false;

/**
 * Outcome of a stale-client check:
 * - `stale`: the deployed version differs from this build (a refresh was requested).
 * - `current`: the deployed version matches this build.
 * - `unknown`: we couldn't determine it (no VERSION, throttled, already
 *   refreshing, a check is in flight, or the version fetch failed).
 *
 * Callers can use `current` to distinguish a genuine server/contract bug from
 * deploy skew (e.g. whether a non-JSON response is worth reporting to Sentry).
 */
export type StaleClientCheck = 'stale' | 'current' | 'unknown';

/**
 * Call this when an API response that returned a successful status code fails
 * client-side Zod schema validation, or returns a non-JSON body where JSON is
 * expected.
 *
 * A well-formed 2xx response that doesn't match the schema this build expects is
 * a strong signal that the running client bundle is stale relative to the
 * deployed API (contract drift) — even when the version delta is only a patch
 * bump, which the normal version check (see `useDashboardVersionCheck` and
 * `multiplayer.ts`) would treat as merely "recommended" and let the user
 * dismiss. We confirm by comparing the origin's `/version.json` to this build's
 * `VERSION`; if they differ we emit `needRefresh(REQUIRED)`, which surfaces a
 * non-dismissible "Refresh" prompt so the user reloads onto a compatible client.
 *
 * Fire-and-forget for the refresh side effect: callers may ignore the return
 * value, or await it to branch on `StaleClientCheck`.
 */
export async function requestRefreshIfClientStale(): Promise<StaleClientCheck> {
  if (!VERSION) return 'unknown';
  if (refreshRequested) return 'unknown';
  // A single check at a time: this collapses a synchronous burst of failing
  // requests into one /version.json fetch (the others return immediately).
  if (inFlight) return 'unknown';
  if (Date.now() - lastCheckedAt < MIN_RECHECK_INTERVAL_MS) return 'unknown';

  inFlight = true;
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    // Don't advance the throttle on a failed fetch (network/5xx) so a transient
    // failure doesn't suppress the next check for the full interval.
    if (!response.ok) return 'unknown';

    const json = (await response.json()) as unknown;
    // We got a usable version.json; throttle subsequent checks from here.
    lastCheckedAt = Date.now();

    if (typeof json !== 'object' || json === null || !('version' in json)) return 'unknown';
    const serverVersion = (json as { version: unknown }).version;
    if (typeof serverVersion !== 'string') return 'unknown';
    if (serverVersion === VERSION) return 'current';

    // The deployed version differs from this build, so the schema mismatch is a
    // stale-client problem rather than a genuine client/server contract bug.
    // Require a refresh regardless of the semver delta.
    refreshRequested = true;
    events.emit('needRefresh', RefreshType.REQUIRED);
    return 'stale';
  } catch {
    // Ignore transient network errors; a later schema failure will retry.
    return 'unknown';
  } finally {
    inFlight = false;
  }
}
